import React from "react";
import PropTypes from "prop-types";
import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import {
  ButtonHOC,
  DefaultBtn,
  DefaultMSFNavBtn,
  DefaultSearchBtn,
} from "../HOCs";
import {
  commonStylesBasic,
  commonStylesIconBasic,
  commonStylesPlaceholderBasic,
  commonStylesPlaceholderMSFNavBtn,
  commonStylesPlaceholderSearchBtn,
  getCommonStylesLabelBasic,
} from "../helpers";
import jssVariables from "../../../../jss_setup";

const commonStyles = {
  ...commonStylesBasic,
};

const ButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderBasic,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  ButtonDefault: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    gridGap: props.showIcon ? commonStyles.gridGap : 0,
    background: jssVariables.colors.base_color,
    color: "white",
    border: `1px solid ${jssVariables.colors.base_color}`,
    borderRadius: 0,
    maxWidth: "100%",
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  ButtonReverted: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    gridGap: props.showIcon ? commonStyles.gridGap : 0,
    background: "white",
    border: `1px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    borderRadius: 0,
    "&:hover": {
      color: "white",
      background: jssVariables.colors.base_color,
    },
    ..._.get(props.customStyles, "ButtonReverted", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    fontSize: props.size ? props.size : "medium",
    fontWeight: props.bold ? "bold" : 700,
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...commonStylesIconBasic,
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    ButtonDefault: (props) => ({
      ..._.get(props.customStyles, "ButtonDefault.smallscreen", {}),
    }),
    ButtonReverted: (props) => ({
      ..._.get(props.customStyles, "ButtonReverted.smallscreen", {}),
    }),
    label: (props) => ({
      ..._.get(props.customStyles, "label.smallscreen", {}),
    }),
  },
});
var Button = ({ text, showIcon, icon, ...props }) => {
  return (
    <DefaultBtn {...props}>
      <span className={props.classes.label}>{text}</span>
      {showIcon && (
        <InlineIcon
          icon={icon ? icon : "fa:angle-right"}
          className={props.classes.icon}
        />
      )}
    </DefaultBtn>
  );
};
Button.defaultProps = {
  text: "",
  showIcon: false,
  classes: {},
  icon: "",
};
Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  showIcon: PropTypes.bool,
  icon: PropTypes.string,
  classes: PropTypes.object,
};
Button = ButtonHOC(Button, ButtonStyles);

// =========================== SearchButton ==========================
const SearchButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderSearchBtn,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  SearchButton: (props) => ({
    ...commonStyles,
    border: `2px solid ${jssVariables.colors.base_color}`,
    background: jssVariables.colors.base_color,
    color: "white",
    height: "2.5rem",
    fontSize: "large",
    minWidth: "7rem",
    borderRadius: 0,
    "&:hover": {
      background: "white",
      color: jssVariables.colors.base_color,
    },
    ..._.get(props.customStyles, "SearchButton", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontWeight: 600,
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    fontSize: "x-large",
    marginTop: ".2rem",
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    SearchButton: (props) => ({
      height: "3rem!important",
      ..._.get(props.customStyles, "SearchButton.smallscreen", {}),
    }),
  },
});
var SearchButton = ({ loading, text, ...props }) => {
  return (
    <DefaultSearchBtn {...props}>
      {loading ? (
        <InlineIcon
          icon="line-md:loading-twotone-loop"
          className={props.classes.icon}
        />
      ) : (
        <span className={props.classes.label}>{text}</span>
      )}
    </DefaultSearchBtn>
  );
};
SearchButton.defaultProps = {
  loading: false,
  text: "",
  classes: {},
};
SearchButton.propTypes = {
  loading: PropTypes.bool,
  text: PropTypes.string.isRequired,
  classes: PropTypes.object,
};
SearchButton = ButtonHOC(SearchButton, SearchButtonStyles);

const MSFNavBtnStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderMSFNavBtn,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  button: (props) => ({
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: "none",
    fontWeight: "normal",
    padding: 0,
    "&:hover:enabled": {
      fontWeight: "bold",
    },
    "&:disabled": {
      fontWeight: 800,
      cursor: "unset",
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    color: jssVariables.colors.base_color,
    fontSize: "large",
    textTransform: "uppercase",
    fontWeight: "inherit",
    ..._.get(props.customStyles, "label", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    button: (props) => ({
      ..._.get(props.customStyles, "button.smallscreen", {}),
    }),
  },
});
var MSFNavBtn = ({ text, ...props }) => {
  return (
    <DefaultMSFNavBtn {...props}>
      <span className={props.classes.label}>{text}</span>
    </DefaultMSFNavBtn>
  );
};
MSFNavBtn.defaultProps = {
  text: "",
  classes: {},
};
MSFNavBtn.propTypes = {
  text: PropTypes.string.isRequired,
  classes: PropTypes.object,
};
MSFNavBtn = ButtonHOC(MSFNavBtn, MSFNavBtnStyles);

export { Button, SearchButton, MSFNavBtn };
